/**
 * useMessageSearch Hook
 * Debounced search hook for chat message search across BYOH hosts.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { searchApi, type SearchResult, type SearchResponse } from '../services/search-api'

export interface UseMessageSearchResult {
  results: SearchResult[]
  total: number
  hostsQueried: number
  hostsResponded: number
  isSearching: boolean
  error: string | null
  query: string
  setQuery: (query: string) => void
  clearSearch: () => void
}

export function useMessageSearch(debounceMs = 300): UseMessageSearchResult {
  const [query, setQueryState] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [hostsQueried, setHostsQueried] = useState(0)
  const [hostsResponded, setHostsResponded] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const abortRef = useRef<(() => void) | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSearch = useCallback(() => {
    if (abortRef.current) {
      abortRef.current()
      abortRef.current = null
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setQueryState('')
    setResults([])
    setTotal(0)
    setHostsQueried(0)
    setHostsResponded(0)
    setIsSearching(false)
    setError(null)
  }, [])

  const executeSearch = useCallback(async (searchQuery: string) => {
    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current()
      abortRef.current = null
    }

    if (searchQuery.length < 2) {
      setResults([])
      setTotal(0)
      setIsSearching(false)
      setError(null)
      return
    }

    setIsSearching(true)
    setError(null)

    const cancellable = searchApi.searchMessages(searchQuery)
    abortRef.current = cancellable.abort

    try {
      const response: SearchResponse = await cancellable.promise
      setResults(response.results)
      setTotal(response.total)
      setHostsQueried(response.hosts_queried)
      setHostsResponded(response.hosts_responded)
    } catch (err) {
      if (err instanceof Error && err.message === 'Request cancelled') return
      setError(err instanceof Error ? err.message : 'Search failed')
      setResults([])
      setTotal(0)
    } finally {
      setIsSearching(false)
      abortRef.current = null
    }
  }, [])

  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (newQuery.length < 2) {
      setResults([])
      setTotal(0)
      setIsSearching(false)
      setError(null)
      return
    }

    setIsSearching(true)
    timerRef.current = setTimeout(() => {
      executeSearch(newQuery)
    }, debounceMs)
  }, [debounceMs, executeSearch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return {
    results,
    total,
    hostsQueried,
    hostsResponded,
    isSearching,
    error,
    query,
    setQuery,
    clearSearch,
  }
}
